import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { LightbulbOutlined, CheckCircleOutline } from '@mui/icons-material';
import PageLayout from '../components/ui/PageLayout';

const CATEGORIES = ['UI Improvement', 'New Feature', 'Bug Fix', 'Integration'];
const PRIORITIES = ['Low', 'Medium', 'High'];

const STORAGE_KEY = 'firmflow_feature_suggestions';

function getSavedSuggestions() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveSuggestion(suggestion) {
  const existing = getSavedSuggestions();
  const entry = { ...suggestion, id: Date.now(), createdAt: new Date().toISOString() };
  existing.unshift(entry);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  return existing;
}

const priorityColor = (p) => {
  if (p === 'High') return 'error';
  if (p === 'Medium') return 'warning';
  return 'default';
};

export default function SuggestFeature() {
  const [form, setForm] = useState({ title: '', category: '', description: '', priority: 'Medium' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    setSuggestions(getSavedSuggestions());
  }, []);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.category) e.category = 'Category is required';
    if (!form.description.trim()) e.description = 'Description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const updated = saveSuggestion(form);
    setSuggestions(updated);
    setSubmitted(true);
  };

  const handleReset = () => {
    setForm({ title: '', category: '', description: '', priority: 'Medium' });
    setErrors({});
    setSubmitted(false);
  };

  return (
    <PageLayout>
      <Box sx={{ maxWidth: 720, mx: 'auto', py: 4, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <LightbulbOutlined color="primary" />
          <Typography variant="h5" fontWeight={600}>Suggest a Feature</Typography>
        </Box>

        {submitted ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleOutline sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Thank you! Your suggestion has been submitted.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We review every suggestion and appreciate your feedback.
            </Typography>
            <Button variant="contained" onClick={handleReset}>
              Submit Another
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Feature Title"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                fullWidth
                required
              />

              <TextField
                select
                label="Category"
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                error={!!errors.category}
                helperText={errors.category}
                fullWidth
                required
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                fullWidth
                required
                multiline
                minRows={3}
              />

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Priority</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {PRIORITIES.map((p) => (
                    <Chip
                      key={p}
                      label={p}
                      color={form.priority === p ? priorityColor(p) : 'default'}
                      variant={form.priority === p ? 'filled' : 'outlined'}
                      onClick={() => update('priority', p)}
                      clickable
                    />
                  ))}
                </Box>
              </Box>

              <Button type="submit" variant="contained" size="large" sx={{ alignSelf: 'flex-start' }}>
                Submit Suggestion
              </Button>
            </Box>
          </Paper>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>Your Previous Suggestions</Typography>
            <List disablePadding>
              {suggestions.map((s) => (
                <ListItem key={s.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">{s.title}</Typography>
                        <Chip label={s.category} size="small" variant="outlined" />
                        <Chip label={s.priority} size="small" color={priorityColor(s.priority)} />
                      </Box>
                    }
                    secondary={s.description}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </PageLayout>
  );
}
